import { useEffect, useState } from 'react';
import { settingsApi } from '../../../lib/apis';

export type PaymentProvider = 'midtrans' | 'doku' | 'stripe' | 'none';
export type PaymentEnvironment = 'sandbox' | 'production';

export type PaymentSettingsPublic = {
  provider: PaymentProvider;
  environment: PaymentEnvironment;
  merchantId: string | null;
  // Optional custom Midtrans base URLs (override environment-derived defaults)
  baseUrlApi?: string | null;
  baseUrlApp?: string | null;
  clientKeyMasked: string | null;
  serverKeyMasked: string | null;
  privateKeyMasked?: string | null;
  lowStockThreshold?: number | null;
  updatedAt: string | null;
};

export type UpdatePaymentSettingsDto = {
  provider: PaymentProvider;
  merchantId?: string;
  clientKey?: string;
  serverKey?: string;
  environment?: PaymentEnvironment;
  baseUrlApi?: string;
  baseUrlApp?: string;
  lowStockThreshold?: number | null;
  privateKey?: string;
};

export function usePaymentSettings() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<PaymentSettingsPublic | null>(null);

  const [provider, setProvider] = useState<PaymentProvider>('none');
  const [environment, setEnvironment] = useState<PaymentEnvironment>('sandbox');
  const [merchantId, setMerchantId] = useState('');
  const [baseUrlApi, setBaseUrlApi] = useState('');
  const [baseUrlApp, setBaseUrlApp] = useState('');
  const [newClientKey, setNewClientKey] = useState('');
  const [newServerKey, setNewServerKey] = useState('');
  const [newPrivateKey, setNewPrivateKey] = useState('');
  const [lowStockThreshold, setLowStockThreshold] = useState<number | null>(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await settingsApi.get<PaymentSettingsPublic>('/admin/api/settings/payment');
      setData(res);
      setProvider(res.provider);
      setEnvironment(res.environment);
      setMerchantId(res.merchantId || '');
      setBaseUrlApi(res.baseUrlApi || '');
      setBaseUrlApp(res.baseUrlApp || '');
      setLowStockThreshold(typeof res.lowStockThreshold === 'number' ? res.lowStockThreshold : (res.lowStockThreshold == null ? null : Number(res.lowStockThreshold)));
      setNewClientKey('');
      setNewServerKey('');
      setNewPrivateKey('');
    } catch (e: any) {
      setError(e.message || 'Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      const body: UpdatePaymentSettingsDto = { provider, environment };
      body.merchantId = merchantId.trim();
      if (baseUrlApi.trim().length > 0) body.baseUrlApi = baseUrlApi.trim();
      if (baseUrlApp.trim().length > 0) body.baseUrlApp = baseUrlApp.trim();
      if (newClientKey.trim().length > 0) body.clientKey = newClientKey.trim();
      if (newServerKey.trim().length > 0) body.serverKey = newServerKey.trim();
      if (newPrivateKey.trim().length > 0) body.privateKey = newPrivateKey.trim();
      if (lowStockThreshold != null && !Number.isNaN(lowStockThreshold)) body.lowStockThreshold = Math.max(0, Math.floor(Number(lowStockThreshold)));
      const res = await settingsApi.put<PaymentSettingsPublic>('/admin/api/settings/payment', body);
      setData(res);
      setNewClientKey('');
      setNewServerKey('');
      setNewPrivateKey('');
    } catch (e: any) {
      setError(e.message || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  return {
    loading,
    saving,
    error,
    data,
    provider,
    setProvider,
    environment,
    setEnvironment,
    merchantId,
    setMerchantId,
    baseUrlApi,
    setBaseUrlApi,
    baseUrlApp,
    setBaseUrlApp,
    newClientKey,
    setNewClientKey,
    newServerKey,
    setNewServerKey,
    newPrivateKey,
    setNewPrivateKey,
    lowStockThreshold,
    setLowStockThreshold,
    reload: load,
    save,
  };
}
